import { pickLocalized, toLocale } from "@core/locale";
import type { SchemaOption } from "../types";

/**
 * ⚠️ TEMPORARY HAND MIRROR — the same shape as the deleted `EDUCATION_LEVEL_CODES` mistake this
 * codebase already learned from once (`rules/rounds.md`: "read reference data from the ROUND,
 * never a hardcoded mirror"). It exists only because the applicant API returns a team member's
 * `time_commitment_code` / `capital_distribution_code` as bare codes with no label — every OTHER
 * catalogue field on this dossier gets its label embedded server-side; these two never did. The
 * one endpoint that resolves them (`GET /intake-rounds/{id}/reference-catalog/`) is
 * administrator/coordinator-only, so an applicant's browser can't fetch them itself. Filed as a
 * comment on `DZCH-50` (2026-08-28) asking Aymen to add `time_commitment_label` /
 * `capital_distribution_label` to the applicant DTO, same pattern already used for
 * `venture.stage_label`. Akram's explicit call to unblock the UI in the meantime, made aware of
 * the tradeoff below.
 *
 * Sourced from `apps/backend/config/acquire/reference-catalog.approved.json`, which is itself
 * marked `"environment": "development-only"` — these codes and labels are NOT guaranteed to match
 * whatever the real production catalogue ends up shipping with.
 *
 * ## Why this is lower-risk than `EDUCATION_LEVEL_CODES` was, not risk-free
 *
 * That mirror fed a value the client SUBMITTED — a stale code became a real `400` on save. Here
 * the codes the applicant picks from are still whatever this table lists, but the value round-trips
 * through the exact same `onCommit` path as every other field, and the BACKEND independently
 * validates it against the round's real catalogue on write — a code this table invents that the
 * round doesn't recognize fails there, loudly, not silently here. The actual risk is a **stale
 * label**: if the round's catalogue changes these codes, this table shows the wrong or a
 * fallen-back-to-raw-code option text until updated by hand. Cosmetic, not data-corrupting — but
 * still exactly the kind of drift the rule exists to avoid, so:
 *
 * **DELETE THIS FILE the moment the backend ships real labels.** Swap `teamTimeCommitmentOptions` /
 * `teamCapitalDistributionOptions` for whatever `TeamMembersPanel` reads once the DTO carries them
 * — nothing else about the two `DossierField` calls that use this should need to change.
 */
interface CatalogRow {
  code: string;
  label_fr: string;
  label_ar: string;
  label_en: string;
}

const TIME_COMMITMENT_ROWS: CatalogRow[] = [
  {
    code: "less_5h",
    label_fr: "Moins de 5 heures par semaine",
    label_ar: "أقل من 5 ساعات أسبوعياً",
    label_en: "Less than 5 hours per week",
  },
  {
    code: "5_10h",
    label_fr: "Environ 5 à 10 heures par semaine",
    label_ar: "حوالي 5 إلى 10 ساعات أسبوعياً",
    label_en: "About 5 to 10 hours per week",
  },
  {
    code: "10_20h",
    label_fr: "Environ 10 à 20 heures par semaine",
    label_ar: "حوالي 10 إلى 20 ساعة أسبوعياً",
    label_en: "About 10 to 20 hours per week",
  },
  {
    code: "more_20h",
    label_fr: "Plus de 20 heures par semaine",
    label_ar: "أكثر من 20 ساعة أسبوعياً",
    label_en: "More than 20 hours per week",
  },
];

const CAPITAL_DISTRIBUTION_ROWS: CatalogRow[] = [
  {
    code: "not_decided",
    label_fr: "Pas encore décidé",
    label_ar: "لم يُقرر بعد",
    label_en: "Not decided yet",
  },
  {
    code: "equal",
    label_fr: "À parts égales",
    label_ar: "بالتساوي",
    label_en: "Equal shares",
  },
  {
    code: "proportional",
    label_fr: "Selon une répartition définie",
    label_ar: "وفق توزيع محدد",
    label_en: "Defined distribution",
  },
  {
    code: "none",
    label_fr: "Pas de capital",
    label_ar: "لا يوجد رأس مال",
    label_en: "No capital",
  },
];

function toOptions(rows: CatalogRow[], language: string): SchemaOption[] {
  const locale = toLocale(language);
  return rows.map((row) => ({
    value: row.code,
    label: pickLocalized(row, "label", locale),
  }));
}

export function teamTimeCommitmentOptions(language: string): SchemaOption[] {
  return toOptions(TIME_COMMITMENT_ROWS, language);
}

export function teamCapitalDistributionOptions(
  language: string
): SchemaOption[] {
  return toOptions(CAPITAL_DISTRIBUTION_ROWS, language);
}
